import { Service } from '../../types';
import { useServiceFlash } from '../../context/LiveContext';
import { Card } from '../common/Card';
import { Sparkline } from '../common/Sparkline';
import { StatusPill } from '../common/StatusPill';
import { UptimeBar } from './UptimeBar';
import {
  statusHex, statusBorderColors, displayUrl, formatSpan, formatDuration, formatRelativeTime,
} from '../../utils/status';

interface Props {
  service: Service;
}

const STRIP_COUNT = 40;

/** Small-caps label used under each metric. */
const LABEL = 'font-mono text-[0.65rem] uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400 mt-1.5';

export function ServiceCard({ service }: Props) {
  const flashing = useServiceFlash(service.id);

  const checks = service.recent_checks ?? [];

  // Oldest → newest for the trace; the strip handles its own ordering.
  const responseSeries = [...checks]
    .reverse()
    .map(c => c.response_time)
    .filter((v): v is number => v !== null);

  // null is "no data yet" (new or paused service), which is not 100 %.
  const hasUptime = service.uptime_24h !== undefined && service.uptime_24h !== null;
  const hasResponse = service.avg_response_time !== null && service.avg_response_time !== undefined;

  // The strip shows the last N checks, so its span depends on the interval —
  // labelling it "24h" would be a guess. Derive the real window instead.
  const stripSpan = formatSpan(STRIP_COUNT * service.interval);

  return (
    <Card
      href={`/services/${service.id}`}
      className={`px-5 pt-[1.15rem] pb-[1.1rem] border-l-[3px] ${statusBorderColors[service.status]} ${
        flashing ? 'animate-flash' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[1.0625rem] font-bold tracking-tight truncate">{service.name}</h3>
          <p
            className="font-mono text-[0.7rem] text-gray-400 dark:text-gray-500 truncate mt-0.5"
            title={service.type === 'http' ? service.url : undefined}
          >
            {displayUrl(service.url, service.type)}
          </p>
        </div>
        <StatusPill status={service.status} />
      </div>

      <div className="flex items-end justify-between gap-5 mt-[1.15rem] mb-4">
        {/* One number leads. Everything else supports it. */}
        <div>
          <p className="font-mono text-[1.75rem] font-medium leading-none tabular-nums">
            {hasUptime ? service.uptime_24h : '—'}
            {hasUptime && <span className="text-base align-baseline">%</span>}
          </p>
          <p className={LABEL}>{hasUptime ? 'uptime · 24h' : 'no data · 24h'}</p>
        </div>

        <div className="text-right min-w-0">
          {responseSeries.length >= 2 && (
            <Sparkline
              values={responseSeries}
              color={statusHex[service.status]}
              className="ml-auto block"
            />
          )}
          <p className="font-mono text-[0.8125rem] tabular-nums text-gray-600 dark:text-gray-300">
            {hasResponse ? formatDuration(service.avg_response_time!) : '—'}
          </p>
          <p className={LABEL}>response · 24h</p>
        </div>
      </div>

      <UptimeBar checks={checks} count={STRIP_COUNT} showNow />

      <div className="flex justify-between mt-2 font-mono text-[0.65rem] uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
        <span>{checks.length > 0 ? `${stripSpan} ago` : 'no history'}</span>
        <span>
          {service.last_checked_at
            ? `checked ${formatRelativeTime(service.last_checked_at)}`
            : 'never checked'}
        </span>
      </div>
    </Card>
  );
}
